import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  IconButton,
  Stack,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { CampagneContent, CibleEntreprise } from '../types/campagne';

interface CampagneFormProps {
  onSubmit: (data: Partial<CampagneContent>) => void;
  onCancel: () => void;
  loading?: boolean;
}

export const CampagneForm: React.FC<CampagneFormProps> = ({ onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState<Partial<CampagneContent>>({
    siteWeb: '',
    cibleEntreprise: {
      secteur: '',
      nombreEmployes: '',
      pays: '',
      exemples: '',
      listeExclusion: '',
    },
    cibleEmployes: '',
    servicesEtProduits: [''],
    problemeResolu: '',
    offreCampagne: '',
    beneficesClients: '',
    objectionsFrequentes: [''],
    differenciateurs: [''],
    concurrents: [''],
    referencesClients: '',
    chiffresClefs: '',
  });

  const handleChange = (field: keyof CampagneContent) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCibleEntrepriseChange = (field: keyof CibleEntreprise) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      cibleEntreprise: {
        ...(prev.cibleEntreprise as CibleEntreprise),
        [field]: event.target.value,
      },
    }));
  };

  const handleArrayChange = (field: keyof CampagneContent, index: number) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => {
      const array = [...(prev[field] as string[])];
      array[index] = event.target.value;
      return {
        ...prev,
        [field]: array,
      };
    });
  };

  const handleAddArrayItem = (field: keyof CampagneContent) => () => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] as string[]), ''],
    }));
  };

  const handleRemoveArrayItem = (field: keyof CampagneContent, index: number) => () => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as string[]).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const renderArrayField = (
    field: keyof CampagneContent,
    label: string,
    placeholder: string
  ) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {label}
      </Typography>
      {(formData[field] as string[]).map((value, index) => (
        <Stack key={index} direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            fullWidth
            value={value}
            onChange={handleArrayChange(field, index)}
            placeholder={`${placeholder} ${index + 1}`}
            disabled={loading}
          />
          <IconButton
            onClick={handleRemoveArrayItem(field, index)}
            disabled={(formData[field] as string[]).length <= 1 || loading}
          >
            <RemoveIcon />
          </IconButton>
          {index === (formData[field] as string[]).length - 1 && (
            <IconButton onClick={handleAddArrayItem(field)} disabled={loading}>
              <AddIcon />
            </IconButton>
          )}
        </Stack>
      ))}
    </Box>
  );

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit}>
        <Typography variant="h6" gutterBottom>
          Informations de base
        </Typography>
        <TextField
          fullWidth
          label="Votre site internet"
          value={formData.siteWeb}
          onChange={handleChange('siteWeb')}
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Typography variant="h6" gutterBottom>
          Ciblage
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Entreprises cibles
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Secteur"
              value={formData.cibleEntreprise?.secteur}
              onChange={handleCibleEntrepriseChange('secteur')}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Nombre d'employés"
              value={formData.cibleEntreprise?.nombreEmployes}
              onChange={handleCibleEntrepriseChange('nombreEmployes')}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Pays"
              value={formData.cibleEntreprise?.pays}
              onChange={handleCibleEntrepriseChange('pays')}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Liste d'exclusion"
              value={formData.cibleEntreprise?.listeExclusion}
              onChange={handleCibleEntrepriseChange('listeExclusion')}
              placeholder="Lien Google Sheet contenant la liste des noms de domaine à exclure"
              helperText="Merci de fournir un lien vers un Google Sheet contenant la liste de tous les noms de domaine de vos clients ou partenaires à ne pas contacter"
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Exemples d'entreprises"
              multiline
              rows={3}
              value={formData.cibleEntreprise?.exemples}
              onChange={handleCibleEntrepriseChange('exemples')}
              placeholder="Listez quelques exemples d'entreprises que vous souhaitez cibler"
              disabled={loading}
            />
          </Stack>
        </Box>

        <TextField
          fullWidth
          label="Employés cibles"
          multiline
          rows={3}
          value={formData.cibleEmployes}
          onChange={handleChange('cibleEmployes')}
          placeholder="Décrivez les profils des personnes que nous devrions contacter"
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          1. Présentation de votre entreprise
        </Typography>
        {renderArrayField(
          'servicesEtProduits',
          'Services/Produits principaux',
          'Service/Produit'
        )}
        <TextField
          fullWidth
          label="Problème résolu"
          multiline
          rows={3}
          value={formData.problemeResolu}
          onChange={handleChange('problemeResolu')}
          placeholder="Quel problème résolvez-vous pour vos clients ?"
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          2. Votre offre
        </Typography>
        <TextField
          fullWidth
          label="Offre de la campagne"
          multiline
          rows={3}
          value={formData.offreCampagne}
          onChange={handleChange('offreCampagne')}
          placeholder="Décrivez l'offre que vous souhaitez mettre en avant"
          sx={{ mb: 3 }}
          disabled={loading}
        />
        <TextField
          fullWidth
          label="Bénéfices clients"
          multiline
          rows={3}
          value={formData.beneficesClients}
          onChange={handleChange('beneficesClients')}
          placeholder="Quels bénéfices concrets vos clients obtiennent-ils ?"
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          3. Positionnement & Différenciation
        </Typography>
        {renderArrayField(
          'objectionsFrequentes',
          'Objections fréquentes',
          'Objection'
        )}
        {renderArrayField(
          'differenciateurs',
          'Éléments différenciateurs',
          'Différenciateur'
        )}
        {renderArrayField(
          'concurrents',
          'Principaux concurrents',
          'Concurrent'
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          4. Preuves & Chiffres clés
        </Typography>
        <TextField
          fullWidth
          label="Références clients"
          multiline
          rows={3}
          value={formData.referencesClients}
          onChange={handleChange('referencesClients')}
          placeholder="Listez vos références clients ou ajoutez des liens"
          sx={{ mb: 3 }}
          disabled={loading}
        />
        <TextField
          fullWidth
          label="Chiffres clés"
          multiline
          rows={3}
          value={formData.chiffresClefs}
          onChange={handleChange('chiffresClefs')}
          placeholder="Partagez vos chiffres clés (nombre de clients, résultats, ancienneté, etc.)"
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Annuler
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Créer la campagne'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
}; 